import React from 'react';
import { Popover } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IPlaneEvent } from '../../../../../store/reducers/scheduleReducer/scheduleReducerTypes';
import { ScheduleType } from '../../../../../proto/generated/api_entities_pb';
import { formatDate, parseStringAsUTCDate } from '../../../../../utils/helpers/dateHelpers/dateHelpers';
import { DisplayAirport, DisplayRoute } from '../../../../../utils/helpers/textDisplayHelpers/DisplayAirport';
import { defineMessages, useIntl } from 'react-intl';
import { CommonFormMessages } from '../../../../../intl/commonMessages/CommonFormMessages';
import PlaneGreyIcon from '../../../../../media/icons/plane-grey.svg?react';
import PaxIcon from "../../../../../media/icons/pax.svg?react";
import PlaneArrivalicon from "../../../../../media/icons/plane-arrival.svg?react";
import FuelIcon from "../../../../../media/icons/fuel.svg?react";
import { LocalTimeDisplay } from '../../../../../components/businessCommon/localTimeDisplay/LocalTimeDisplay';
import { useWindowSize } from '../../../../../hooks/useWindowSize';

dayjs.extend(utc);

const EventPopoverMessages = defineMessages({
  event: {
    id: "eventPopover.event",
    defaultMessage: "Event",
  },
  plane: {
    id: "eventPopover.plane",
    defaultMessage: "Plane",
  },
  airport: {
    id: "eventPopover.airport",
    defaultMessage: "Airport",
  },
  route: {
    id: "eventPopover.route",
    defaultMessage: "Route",
  },
  period: {
    id: "eventPopover.period",
    defaultMessage: "Period",
  },
  dateFrom: {
    id: "eventPopover.dateFrom",
    defaultMessage: "From",
  },
  dateTo: {
    id: "eventPopover.dateTo",
    defaultMessage: "To",
  },
  schedule: {
    id: "eventPopover.schedule",
    defaultMessage: "Расписание",
  },
  scheduledTime: {
    id: "eventPopover.scheduledTime",
    defaultMessage: "Плановое время (UTC)",
  },
  actualTime: {
    id: "eventPopover.actualTime",
    defaultMessage: "Фактическое время (UTC)",
  },
  delay: {
    id: "eventPopover.delay",
    defaultMessage: "Задержка",
  },
  hoursMinutesLeft: {
    id: "eventPopover.hoursMinutesLeft",
    defaultMessage: "{hours} hr {minutes} min left",
  },
  minutesLeft: {
    id: "eventPopover.minutesLeft",
    defaultMessage: "{minutes} min left",
  },
});

// Общие стили
const BaseText = styled.div`
  color: #000;
  font-style: normal;
`;

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 12px;
  }
`;

const PlaneName = styled(BaseText)`
  font-size: 16px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.14px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ScheduleTitle = styled(BaseText)`
  font-size: 16px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.14px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RouteContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const AirportContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const AirportInfo = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${props => props.$alignRight && 'align-items: flex-end;'}
`;

const DateText = styled(BaseText)`
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.12px;
  opacity: 0.5;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const LocalTimeWrapper = styled.div`
  margin-top: -4px;
`;

const AirportCode = styled(BaseText)`
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.16px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const AirportSubtitle = styled(BaseText)<{ $alignRight?: boolean }>`
  font-size: 13px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.11px;
  ${props => props.$alignRight && 'text-align: right;'}

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const FlightLineContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  position: relative;
  flex: 1;

  @media (max-width: 768px) {
    gap: 4px;
    min-width: 60px;
  }
`;

const DurationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const DurationText = styled.div`
  color: #000;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.11px;
  opacity: 0.5;
  white-space: nowrap;
`;

const PlaneIconContainer = styled.div<{ $alignLeft?: boolean; $alignRight?: boolean; $hideLeft?: boolean; $hideRight?: boolean; $progressPercent?: number }>`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: ${props => {
    if (props.$progressPercent !== undefined) {
      return 'flex-start';
    }
    return props.$alignLeft ? 'flex-start' : props.$alignRight ? 'flex-end' : 'center';
  }};
  width: 100%;
  height: 20px;
`;

const PlaneIconWrapper = styled.div<{ $progressPercent?: number }>`
  position: ${props => props.$progressPercent !== undefined ? 'absolute' : 'relative'};
  left: ${props => {
    if (props.$progressPercent === undefined) return 'auto';
    // Учитываем точки по краям (по 5px с каждой стороны) и позиционируем иконку
    // Процент применяется к ширине контейнера, но нужно учесть центрирование иконки
    return `${props.$progressPercent}%`;
  }};
  transform: ${props => props.$progressPercent !== undefined ? 'translateX(-50%)' : 'none'};
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  padding: 0 8px;
  background: #fff;
`;

const LineWithDotContainer = styled.div<{ $position: 'left' | 'right'; $hidden?: boolean }>`
  position: relative;
  display: ${props => props.$hidden ? 'none' : 'flex'};
  align-items: center;
  flex: ${props => props.$hidden ? '0' : '1'};
`;

const FlightLine = styled.div`
  height: 2px;
  background-color: #808080;
  flex: 1;
`;

const FlightDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #808080;
  flex-shrink: 0;
`;

const EventTypeTag = styled.div`
  display: inline-block;
  padding: 4px 12px;
  border: 1px solid #722ED1;
  border-radius: 4px;
  color: #722ED1;
  font-size: 12px;
  font-weight: 500;

  @media (max-width: 768px) {
    padding: 3px 8px;
    font-size: 11px;
  }
`;

const InfoSectionsContainer = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoSectionText = styled.span`
  color: #000;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.11px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const ScheduleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ScheduleTableHeader = styled.th`
  color: #808080;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  text-align: left;
  padding: 8px 0;
  border-bottom: 1px solid #E0E0E0;

  @media (max-width: 768px) {
    font-size: 11px;
    padding: 6px 0;
  }
`;

const ScheduleTableCell = styled.td`
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.12px;
  padding: 8px 0;
  border-bottom: 1px solid #E0E0E0;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 6px 0;
  }
`;

const formatRemainingFlightTime = (diffMs: number, intl: ReturnType<typeof useIntl>) => {
  const clampedDiff = Math.max(diffMs, 0);
  const totalMinutes = Math.floor(clampedDiff / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return intl.formatMessage(EventPopoverMessages.hoursMinutesLeft, { hours, minutes });
  }

  return intl.formatMessage(EventPopoverMessages.minutesLeft, { minutes });
};

interface EventPopoverProps {
  event: IPlaneEvent;
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  children: React.ReactNode;
}

export const EventPopover: React.FC<EventPopoverProps> = ({
  event,
  visible,
  onVisibleChange,
  children,
}) => {
  const intl = useIntl();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  // Определяем текст типа события (точно такой же как в радио-кнопках при создании)
  let eventTypeText: string;
  if (event.eventType === ScheduleType.CUSTOM) {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeCustomRadio);
  } else if (event.eventType === ScheduleType.EMPTY_LEG) {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio);
  } else if (event.eventType === ScheduleType.ORDER_REQUEST) {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio);
  } else if (event.eventType === ScheduleType.MAINTENANCE) {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio);
  } else if (event.eventType === ScheduleType.OTHER) {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeParkingRadio);
  } else {
    eventTypeText = intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio);
  }

  // Определяем статус рейса
  // Правильно парсим даты как UTC
  const now = dayjs();
  const dateFromDate = typeof event.dateFrom === 'string' 
    ? parseStringAsUTCDate(event.dateFrom) 
    : event.dateFrom;
  const dateToDate = typeof event.dateTo === 'string'
    ? parseStringAsUTCDate(event.dateTo)
    : event.dateTo;
  const dateFrom = dayjs.utc(dateFromDate);
  const dateTo = dayjs.utc(dateToDate);
  
  // Функция для форматирования даты в UTC (события хранятся в UTC)
  // Принимает Date или string, правильно парсит и форматирует как UTC
  const formatLocalDate = (dateInput: Date | string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
    // Если это строка, парсим её как UTC
    let date: Date;
    if (typeof dateInput === 'string') {
      date = parseStringAsUTCDate(dateInput);
    } else {
      date = dateInput;
    }
    
    const pad = (num: number) => String(num).padStart(2, '0');
    // Используем UTC методы, так как события хранятся в UTC
    const dd = pad(date.getUTCDate());
    const mm = pad(date.getUTCMonth() + 1);
    const yyyy = date.getUTCFullYear();
    const hh = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    
    switch (format) {
      case 'date':
        return `${dd}.${mm}.${yyyy}`;
      case 'time':
        return `${hh}:${minutes}`;
      default:
        return `${dd}.${mm}.${yyyy} ${hh}:${minutes}`;
    }
  };
  
  const isFlightNotStarted = now.isBefore(dateFrom);
  const isFlightFinished = now.isAfter(dateTo);
  const isFlightInProgress = !isFlightNotStarted && !isFlightFinished;

  const totalDurationMs = dateTo.diff(dateFrom);

  // Вычисляем процент прогресса полета
  let flightProgressPercent: number | undefined = undefined;
  if (isFlightInProgress) {
    if (totalDurationMs > 0) {
      const elapsedTimeMs = now.diff(dateFrom);
      flightProgressPercent = Math.min(100, Math.max(0, (elapsedTimeMs / totalDurationMs) * 100));
    } else {
      flightProgressPercent = 100;
    }
  }

  const remainingDurationText = isFlightInProgress
    ? formatRemainingFlightTime(dateTo.diff(now), intl)
    : '';

  // Формируем заголовок: [номер самолёта] - [название самолёта (модель)] - [название события, если есть]
  // Формат: registrationNumber - planeTypeName - eventName
  const planeTitleParts = [event.plane.registrationNumber];
  
  // Добавляем модель самолёта, если она есть
  if (event.plane.planeTypeName) {
    planeTitleParts.push(event.plane.planeTypeName);
  }
  
  // Добавляем название события, если оно есть
  if (event.eventName) {
    planeTitleParts.push(event.eventName);
  }
  
  const planeTitle = planeTitleParts.join(' - ');

  const content = (
    <ContentWrapper>
      <PlaneName>
        {planeTitle}
      </PlaneName>

      {/* Визуализация маршрута */}
      {event.airportFrom && event.airportTo && (
        <RouteContainer>
          {/* Первый контейнер - аэропорт отправления */}
          <AirportContainer>
            <AirportInfo>
              <DateText>
                {formatLocalDate(event.dateFrom, 'date')} {formatLocalDate(event.dateFrom, 'time')}
              </DateText>
              <LocalTimeWrapper>
                <LocalTimeDisplay
                  utcDate={dateFrom}
                  airportOffset={event.airportFrom?.timezone?.offset}
                />
              </LocalTimeWrapper>
              <AirportCode>
                {event.airportFrom.icaoCode}
              </AirportCode>
              <AirportSubtitle>
                {DisplayAirport(event.airportFrom, 'location_and_name')}
              </AirportSubtitle>
            </AirportInfo>
          </AirportContainer>

          {/* Второй контейнер - линия с самолетом */}
          <FlightLineContainer>
            {isFlightInProgress && (
              <DurationContainer>
                <DurationText>
                  {remainingDurationText}
                </DurationText>
              </DurationContainer>
            )}
            <PlaneIconContainer 
              $alignLeft={isFlightNotStarted} 
              $alignRight={isFlightFinished}
              $hideLeft={isFlightNotStarted}
              $hideRight={isFlightFinished}
              $progressPercent={flightProgressPercent}
            >
              <LineWithDotContainer $position="left" $hidden={isFlightNotStarted}>
                <FlightDot />
                <FlightLine />
              </LineWithDotContainer>
              <PlaneIconWrapper $progressPercent={flightProgressPercent}>
                <PlaneGreyIcon style={{ width: isMobile ? 16 : 20, height: isMobile ? 16 : 20 }} />
              </PlaneIconWrapper>
              <LineWithDotContainer $position="right" $hidden={isFlightFinished}>
                <FlightLine />
                <FlightDot />
              </LineWithDotContainer>
            </PlaneIconContainer>
          </FlightLineContainer>

          {/* Третий контейнер - аэропорт прибытия */}
          <AirportContainer>
            <AirportInfo $alignRight>
              <DateText>
                {formatLocalDate(event.dateTo, 'date')} {formatLocalDate(event.dateTo, 'time')}
              </DateText>
              <LocalTimeWrapper>
                <LocalTimeDisplay
                  utcDate={dateTo}
                  airportOffset={event.airportTo?.timezone?.offset}
                />
              </LocalTimeWrapper>
              <AirportCode>
                {event.airportTo.icaoCode}
              </AirportCode>
              <AirportSubtitle $alignRight>
                {DisplayAirport(event.airportTo, 'location_and_name')}
              </AirportSubtitle>
            </AirportInfo>
          </AirportContainer>
        </RouteContainer>
      )}

      {/* Тег типа события и информация */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <EventTypeTag>
          {eventTypeText}
        </EventTypeTag>
        <InfoSectionsContainer>
          {event.pax !== undefined && event.pax !== null && (
            <InfoSection>
              <PaxIcon />
              <InfoSectionText>{event.pax}</InfoSectionText>
            </InfoSection>
          )}
          {event.refuelingCount !== undefined && event.refuelingCount !== null && (
            <>
              <InfoSection>
                <PlaneArrivalicon />
                <InfoSectionText>{event.refuelingCount}</InfoSectionText>
              </InfoSection>
              <InfoSection>
                <FuelIcon />
                <InfoSectionText>{event.refuelingCount}</InfoSectionText>
              </InfoSection>
            </>
          )}
        </InfoSectionsContainer>
      </div>

      {/* Секция расписания */}
      <ScheduleContainer>
        <ScheduleTitle>
          {intl.formatMessage(EventPopoverMessages.schedule)}
        </ScheduleTitle>
        <ScheduleTable>
          <thead>
            <tr>
              <ScheduleTableHeader>{intl.formatMessage(EventPopoverMessages.scheduledTime)}</ScheduleTableHeader>
              <ScheduleTableHeader>{intl.formatMessage(EventPopoverMessages.actualTime)}</ScheduleTableHeader>
              <ScheduleTableHeader>{intl.formatMessage(EventPopoverMessages.delay)}</ScheduleTableHeader>
            </tr>
          </thead>
          <tbody>
            <tr>
              <ScheduleTableCell>{formatLocalDate(event.dateFrom, 'date')} {formatLocalDate(event.dateFrom, 'time')}</ScheduleTableCell>
              <ScheduleTableCell>{formatLocalDate(event.dateFrom, 'date')} {formatLocalDate(event.dateFrom, 'time')}</ScheduleTableCell>
              <ScheduleTableCell>0:00</ScheduleTableCell>
            </tr>
            <tr>
              <ScheduleTableCell>{formatLocalDate(event.dateTo, 'date')} {formatLocalDate(event.dateTo, 'time')}</ScheduleTableCell>
              <ScheduleTableCell>{formatLocalDate(event.dateTo, 'date')} {formatLocalDate(event.dateTo, 'time')}</ScheduleTableCell>
              <ScheduleTableCell>0:00</ScheduleTableCell>
            </tr>
          </tbody>
        </ScheduleTable>
      </ScheduleContainer>
    </ContentWrapper>
  );

  return (
    <Popover
      content={content}
      visible={visible}
      onVisibleChange={onVisibleChange}
      trigger={[]} // Убираем автоматические триггеры, управляем только через состояние
      placement="top"
      align={{
        offset: [isMobile ? 50 : 0, -8], // Смещение для лучшего позиционирования (на мобильном сдвигаем вправо, отрицательное значение поднимает выше)
      }}
      overlayClassName="schedule-event-popover"
      overlayStyle={{
        zIndex: 1000,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
        borderRadius: '8px',
        paddingBottom: 0,
      }}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: '8px',
        background: '#FFF',
        minWidth: isMobile ? 280 : 600,
        maxWidth: isMobile ? 320 : 700,
      }}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      destroyTooltipOnHide={false}
      // Для Safari: используем builtinPlacements для более стабильного позиционирования
      builtinPlacements={{
        top: {
          points: ['bc', 'tc'],
          offset: [isMobile ? 50 : 0, -16],
          overflow: {
            adjustX: true,
            adjustY: true,
          },
        },
      }}
    >
      {children}
    </Popover>
  );
};

